import { H1, Icon, Overlay, Tag } from "@blueprintjs/core"
import React, { useCallback, useEffect, useState } from "react"
import classNames from "classnames"

import cssUtils from "./utilsCss.module.css"
import css from "./NotificationsPermissionsRequest.module.css"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"

interface MicrophonePermissionRequestProps {
  onPermissionGranted: () => void
  onClose: () => void
}

const MicrophonePermissionRequest: React.FC<
  MicrophonePermissionRequestProps
> = ({ onPermissionGranted, onClose }) => {
  const { t } = useTranslation()
  const [overlayOpen, setOverlayOpen] = useState(false)
  const [permissionDenied, setPermissionDenied] = useState(false)

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false)
    onClose()
  }, [onClose])

  useEffect(() => {
    const showMicrophonePermissionRequest = async () => {
      const onAllowMicrophone = async () => {
        setOverlayOpen(true)
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          })
          // Permission granted, clean up test stream and call callback
          stream.getTracks().forEach((track) => track.stop()) // Clean up the test stream
          // Close overlay first, then call the permission granted callback
          setOverlayOpen(false)
          onPermissionGranted()
          // Close the component after a brief delay
          setTimeout(() => {
            onClose()
          }, 100)
        } catch (error) {
          // Permission was denied or error occurred, keep overlay open to show message
          console.error("Microphone permission denied:", error)
          setPermissionDenied(true)
          // Don't close immediately, let user see the guidance
        }
      }

      // Always show the toast when this component is rendered
      // The parent component decides when to show this based on permission state
      window.appToaster.show(
        {
          message: i18n.t("app.microphone.request"),
          intent: "primary",
          timeout: 0,
          icon: "microphone",
          action: {
            text: i18n.t("app.actions.enable"),
            onClick: onAllowMicrophone,
            intent: "primary",
            className: cssUtils.underlineText,
          },
        },
        "microphone-permission",
      )
    }

    showMicrophonePermissionRequest()
  }, [closeOverlay, onPermissionGranted])

  if (!overlayOpen) return null

  return (
    <Overlay
      isOpen={overlayOpen}
      enforceFocus={true}
      onClose={closeOverlay}
      canOutsideClickClose={false}
    >
      <div className={classNames(css.container, cssUtils.center)}>
        <div>
          <Icon icon="arrow-top-left" size={48} />
        </div>
        <div className={css.info}>
          <H1>
            {permissionDenied
              ? t("app.microphone.blockedTitle")
              : t("app.microphone.allowTitle")}
          </H1>
          <p>
            {permissionDenied
              ? t("app.microphone.blockedText")
              : t("app.microphone.allowText")}
          </p>
          <Tag onClick={closeOverlay} interactive large round>
            {t("app.actions.gotIt")}
          </Tag>
        </div>
      </div>
    </Overlay>
  )
}

export default MicrophonePermissionRequest
